import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AccountLinkModel } from 'src/Libs/installation-rights-management/models'
import { getAccountLinkList } from '../api'

export const accountLinksQueryKey = (installationId: number | string | undefined) => ['installations', installationId, 'account-links'] as const

/**
 * Hook to get account link by specific account id
 * @param installationId - Installation id
 * @param accountId - Account id
 */
export const useAccountLinkByAccountId = (): ((
  installationId: number | string | undefined,
  accountId: number | string | undefined
) => Promise<AccountLinkModel | null>) => {
  const authFetch = useAuthFetchWithResponse()
  const queryClient = useQueryClient()

  return useCallback(
    async (installationId: number | string | undefined, accountId: number | string | undefined): Promise<AccountLinkModel | null> => {
      if (installationId === undefined || accountId === undefined) {
        return null
      }

      return queryClient.fetchQuery({
        queryKey: accountLinksQueryKey(installationId),
        queryFn: async () => {
          const result = await getAccountLinkList(`${installationId}`, authFetch)

          if (result.hasErrors) {
            throw result.error ?? new Error('Unexpected error occurred while fetching account link')
          }

          return result.value?.find(accountLink => accountLink.accountId.toString() === accountId.toString())
        },
      })
    },
    [authFetch, queryClient]
  )
}
